import React, { FC, useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InputAdornment, Link } from "@mui/material";

import { EmailGreyIcon } from "@/assets/icons";
import { ItsMeButton, TextInput } from "@/components/atoms";
import { PasswordInput } from "@/components/molecules";
import { useNavigation } from "@/shared/hooks";

import { legacyPasswordValidationSchema, PasswordFormType } from "../../../shared";
import { StyledDescription } from "./styled";

interface FormType extends PasswordFormType {
    email: string;
}

type InviteIdentifyAccountProps = {
    userStatus: MyBrokerApi.UserStatus;
    onIdentifyAccount: (password: string) => void;
    onItsmeActivateAccount: () => void;
    isFetching: boolean;
    wrongPassword?: boolean;
};

const InviteIdentifyAccount: FC<InviteIdentifyAccountProps> = ({
    userStatus,
    onIdentifyAccount,
    onItsmeActivateAccount,
    isFetching,
    wrongPassword = false,
}) => {
    const { t } = useTranslation();
    const { navToResetPassword } = useNavigation();
    const { email, hasItsMe } = userStatus;

    const defaultValues = {
        password: "",
        email,
    };

    const {
        handleSubmit,
        setError,
        setValue,
        control,
        formState: { isValid, isSubmitting },
    } = useForm<FormType>({
        defaultValues,
        resolver: yupResolver(legacyPasswordValidationSchema),
    });

    const onSubmit = (formValues: FormType) => {
        onIdentifyAccount(formValues.password);
        return null;
    };

    useEffect(() => {
        if (wrongPassword) {
            setError("password", { type: "authenticate", message: t("Login.Authenticate.DoNotMatch") });
        }
    }, [wrongPassword]);

    const handleResetPassword = () => {
        return navToResetPassword(userStatus);
    };

    return (
        <>
            <div className="title">{t("Invite.IdentifyUser.Title")}</div>
            <StyledDescription>
                {hasItsMe ? t("Invite.IdentifyUser.DescriptionItsme") : t("Invite.IdentifyUser.Description")}
            </StyledDescription>
            <div>
                {hasItsMe ? (
                    <ItsMeButton
                        onClick={onItsmeActivateAccount}
                        className="d-flex full-width mt48"
                        label={t("Invite.DefinePassword.UseItsme")}
                    />
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextInput
                            label={t("Login.Authenticate.YourEmail")}
                            LabelProps={{ className: "mt16" }}
                            disabled
                            name="email"
                            autoComplete="email"
                            control={control}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailGreyIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <PasswordInput
                            name="password"
                            label={t("Login.Authenticate.YourPassword")}
                            isNew={false}
                            placeholder={t("ResetPassword.NewPassword.Placeholder")}
                            control={control}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setValue("password", event.target.value, { shouldValidate: true });
                            }}
                        />
                        <Link
                            onClick={handleResetPassword}
                            className="hyperlink"
                            fontSize="var(--size-16)"
                            fontWeight="bold"
                            display="block"
                            margin="5px auto"
                            width="fit-content"
                        >
                            {t("Login.Authenticate.ForgotPassword")}
                        </Link>
                        <div className="form-bottom-container end">
                            <Button disabled={isFetching || isSubmitting || !isValid} type="submit" fullWidth>
                                {t("Login.Authenticate.Login")}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </>
    );
};

export default InviteIdentifyAccount;
